import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FilmSlate, Presentation, FileArrowDown, PlayCircle, Handshake, Clock } from "@phosphor-icons/react";
import { youtubeThumb } from "../lib/youtube";

const meta = {
  video: { icon: FilmSlate, label: "VIDEO" },
  presentation: { icon: Presentation, label: "PRESENTATION" },
  other: { icon: FileArrowDown, label: "RESOURCE" },
  sponsor: { icon: Handshake, label: "PARTNER" },
};

// Category-specific placeholder POOLS shown when a content has no custom thumbnail
// (and cannot be resolved from YouTube/Vimeo). Each card gets a deterministic
// image from its category pool based on its `id`, so the library never looks
// monotonous. Auto-generated PDF/PPTX covers (from thumbnail_service.py) still
// take priority via `item.thumbnail`.
const CATEGORY_FALLBACKS = {
  video: [
    "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    "https://images.unsplash.com/photo-1666214280391-8ff5bd3c0bf0?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    "https://images.unsplash.com/photo-1584982751601-97dcc096659c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    "https://images.unsplash.com/photo-1551076805-e1869033e561?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    "https://images.unsplash.com/photo-1579684385127-1ef15d508118?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    "https://images.unsplash.com/photo-1530026405186-ed1f139313f8?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
  ],
  presentation: [
    "https://images.unsplash.com/photo-1475721027785-f74eccf877e2?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    "https://images.unsplash.com/photo-1591115765373-5207764f72e7?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    "https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    "https://images.unsplash.com/photo-1523240795612-9a054b0db644?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    "https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
  ],
  other: [
    "https://images.unsplash.com/photo-1568667256549-094345857637?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    "https://images.unsplash.com/photo-1571310100246-e0676f359b42?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    "https://images.unsplash.com/photo-1519669556878-63bdad8a1a49?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
  ],
  sponsor: [
    "https://images.unsplash.com/photo-1497215728101-856f4ea42174?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
  ],
};

// Simple deterministic hash of a string → non-negative integer.
function hashString(s) {
  let h = 0;
  const str = String(s || "");
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h) + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function pickCategoryFallback(category, id) {
  const pool = CATEGORY_FALLBACKS[category] || CATEGORY_FALLBACKS.other;
  const idx = hashString(id) % pool.length;
  return pool[idx];
}

function vimeoId(url) {
  if (!url) return null;
  const m = url.match(/(?:vimeo\.com\/)(?:.*\/)?(\d+)/);
  return m ? m[1] : null;
}

function fmtDuration(sec) {
  if (!sec && sec !== 0) return null;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

export default function ContentCard({ item, index = 0, span = "" }) {
  const M = meta[item.category] || meta.other;
  const Icon = M.icon;
  const img = item.thumbnail
    || (item.youtube_url ? youtubeThumb(item.youtube_url) : null)
    || pickCategoryFallback(item.category, item.id);
  const [preview, setPreview] = useState(false);
  const vid = item.vimeo_url ? vimeoId(item.vimeo_url) : null;
  const canPreview = item.category === "video" && vid;
  const duration = fmtDuration(item.duration);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 6) * 0.06, ease: [0.16, 1, 0.3, 1] }}
      className={span}
    >
      <Link
        to={`/content/${item.id}`}
        data-testid={`content-card-${item.id}`}
        onMouseEnter={() => canPreview && setPreview(true)}
        onMouseLeave={() => setPreview(false)}
        className={`group relative flex h-full flex-col overflow-hidden border bg-ink-700/40 transition-all duration-500 hover:shadow-[0_0_40px_rgba(0,240,255,0.08)] ${item.featured ? "border-cyan/50 shadow-[0_0_30px_rgba(0,240,255,0.08)]" : "border-white/10 hover:border-cyan/40"}`}
      >
        {item.featured && (
          <span data-testid={`featured-badge-${item.id}`} className="absolute right-3 top-3 z-20 flex items-center gap-1 bg-cyan px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-ink-900">
            ★ Featured
          </span>
        )}
        <div className="relative aspect-video overflow-hidden">
          <img
            src={img}
            alt={item.title}
            className="h-full w-full object-cover opacity-60 transition-all duration-700 group-hover:scale-105 group-hover:opacity-80"
          />
          {preview && canPreview && (
            <iframe
              data-testid={`preview-${item.id}`}
              title={`preview-${item.id}`}
              src={`https://player.vimeo.com/video/${vid}?background=1&autoplay=1&muted=1&loop=1&autopause=0`}
              className="absolute inset-0 z-10 h-full w-full"
              style={{ pointerEvents: "none" }}
              allow="autoplay"
              frameBorder="0"
            />
          )}
          <div className="absolute inset-0 bg-ink-900/40 mix-blend-multiply" />
          <div className="absolute left-4 top-4 z-20 flex items-center gap-2 border border-white/20 bg-ink-900/60 px-2.5 py-1 backdrop-blur">
            <Icon size={13} className="text-cyan" />
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-200">{M.label}</span>
          </div>
          {duration && (
            <div data-testid={`duration-${item.id}`} className="absolute bottom-3 right-3 z-20 flex items-center gap-1 bg-ink-900/80 px-2 py-0.5 font-mono text-[10px] font-medium text-cyan backdrop-blur">
              <Clock size={11} weight="bold" /> {duration}
            </div>
          )}
          {item.category === "video" && !preview && (
            <div className="absolute inset-0 z-10 flex items-center justify-center opacity-0 transition-opacity duration-500 group-hover:opacity-100">
              <PlayCircle size={64} weight="thin" className="text-cyan drop-shadow-[0_0_20px_rgba(0,240,255,0.6)]" />
            </div>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-2 p-6">
          <h3 className="font-head text-lg font-semibold leading-tight text-slate-100 transition-colors group-hover:text-cyan">
            {item.title}
          </h3>
          {item.speaker && (
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan/70">{item.speaker}</p>
          )}
          {fmtDate(item.recorded_at) && (
            <p data-testid={`recorded-${item.id}`} className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              {fmtDate(item.recorded_at)}
            </p>
          )}
          {item.description && (
            <p className="mt-1 line-clamp-2 font-body text-sm leading-relaxed text-slate-400">{item.description}</p>
          )}
          {item.tags && item.tags.length > 0 && (
            <div className="mt-auto flex flex-wrap gap-1.5 pt-3">
              {item.tags.slice(0, 3).map((tg) => (
                <span key={tg} className="rounded-full border border-white/10 px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.1em] text-slate-400">{tg}</span>
              ))}
            </div>
          )}
        </div>
        <span className="absolute right-0 top-0 z-20 h-[2px] w-0 bg-cyan transition-all duration-500 group-hover:w-full" />
      </Link>
    </motion.div>
  );
}
